import { NgClass } from "@angular/common";
import { Component } from "@angular/core";
import { PonderAuthClient } from "@apiserver-rest/ponder-auth-client";
import { AuthResponse, ModifyPermissionsRequest, NullableOfUserRoleType, UserPermissionType, UserRoleAndPermissions, UserRoleType } from "@apiserver-rest/ponder-auth-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { Subscription, take } from "rxjs";

@Component({
    selector: "edit-user-dialog",
    imports: [NgClass, LoadingComponent],
    templateUrl: "./edit-user-dialog.component.html"
})
export class EditUserDialogComponent {
    public isVisible: boolean = false;
    public user?: UserRoleAndPermissions;

    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    public readonly roleTypes = Object.values(UserRoleType);
    public readonly allPermissions = Object.values(UserPermissionType);

    public addablePermissions: UserPermissionType[] = [];       // Permissions that can be added (not already assigned)
    public currPermissions: UserPermissionType[] = [];          // Currently assigned permissions
    public addedPermissions: UserPermissionType[] = [];         // New permissions to be added
    public removedPermissions: UserPermissionType[] = [];       // Permission to be removed
    public roleChange?: UserRoleType;

    public isSaving: boolean = false;
    public saveSuccessful: boolean = true;

    private editUserSub!: Subscription;

    constructor(private ponderAuthClient: PonderAuthClient, events: EventBroker) {
        this.editUserSub = events.admin.editUserEvent.subscribe({
            next: (user: UserRoleAndPermissions) => {
                this.isVisible = true;
                this.user = user;

                this.currPermissions = (this.user?.permissions ? [...this.user.permissions] : []);
                this.currPermissions.sort();

                this.allPermissions.forEach(p => {
                    if (this.currPermissions.indexOf(p) < 0) {
                        this.addablePermissions.push(p);
                    }
                });
            }
        });
    }

    public ngOnDestroy(): void {
        this.editUserSub.unsubscribe();
    }
    
    public onClose(): void {
        this.isVisible = false;
        this.user = undefined;
        this.currPermissions = [];
        this.addedPermissions = [];
        this.removedPermissions = [];
        this.addablePermissions = [];
        this.roleChange = undefined;
        this.isSaving = false;
        this.saveSuccessful = true;
    }

    public onCancelClicked(): void {
        this.onClose();
    }

    public onSaveClicked(): void {
        if (this.isSaving) {
            return;
        }

        this.isSaving = true;
        this.saveSuccessful = true;
        
        // Right now only allow modification of the role and permissions.
        // In the future need an "edit user" endpoint that allows role, permission, username, email address, state (ban), etc.
        var request = new ModifyPermissionsRequest({
            username: this.user!.user.username,
            roleChange: (this.roleChange as string | undefined) as NullableOfUserRoleType,
            grantedPermissions: this.addedPermissions,
            revokedPermissions: this.removedPermissions
        });

        this.ponderAuthClient
            .modify(request)
            .pipe(take(1))
            .subscribe({
                next: (response: AuthResponse) => {
                    if (response.success) {
                        this.onClose();
                    } else {
                        this.isSaving = false;
                        this.saveSuccessful = false;
                    }
                },
                error: () => {
                    this.isSaving = false;
                    this.saveSuccessful = false;
                }
            });
        
    }

    public onRemovePermission(permission: UserPermissionType): void {
        var currIndex = this.currPermissions.indexOf(permission);

        if (currIndex >= 0) {
            this.currPermissions.splice(currIndex, 1);
            this.addablePermissions.push(permission);

            var addedIndex = this.addedPermissions.indexOf(permission);

            if (addedIndex >= 0) {
                this.addedPermissions.splice(addedIndex, 1);
            }

            var origIndex = this.user!.permissions?.indexOf(permission) ?? -1;

            if (origIndex >= 0) {
                // Only remove if this is an originally assigned permission, and not one added this session (undone).
                this.removedPermissions.push(permission);
            }
        }
    }

    public onAddPermission(permission: UserPermissionType | string): void {
        var addedPermission = permission as UserPermissionType;

        var currIndex = this.currPermissions.indexOf(addedPermission);

        if (currIndex < 0) {
            this.currPermissions.push(addedPermission);
            this.currPermissions.sort();

            var origIndex = this.user!.permissions?.indexOf(addedPermission) ?? -1;
    
            if (origIndex < 0) {
                // Only add as added if not one of the original permissions assigned.
                this.addedPermissions.push(addedPermission);
            }
        }

        var removedIndex = this.removedPermissions.indexOf(addedPermission);

        if (removedIndex >= 0) {
            this.removedPermissions.splice(removedIndex, 1);
        }

        var addableIndex = this.addablePermissions.indexOf(addedPermission);

        if (addableIndex >= 0) {
            this.addablePermissions.splice(addableIndex, 1);
        }
    }

    public onRoleSelected(roleStr: string): void {
        var role = roleStr as UserRoleType;

        if (role != this.user!.role) {
            this.roleChange = role;
        } else {
            this.roleChange = undefined;
        }
    }
}